import { useEffect, useState, useMemo } from 'react';
import { adminApi } from '../../lib/api.js';
import { useToast } from '../../hooks/useToast.jsx';
import DataTable from '../../components/ui/DataTable.jsx';
import Input from '../../components/ui/Input.jsx';
import Select from '../../components/ui/Select.jsx';
import Modal from '../../components/ui/Modal.jsx';
import Badge from '../../components/ui/Badge.jsx';
import Button from '../../components/ui/Button.jsx';

function formatTimestamp(ts) {
  if (!ts) return '\u2014';
  try {
    const d = new Date(ts);
    return d.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    });
  } catch {
    return ts;
  }
}

function formatAction(action) {
  if (!action) return '\u2014';
  return action.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

const ACTION_VARIANTS = {
  create: 'success',
  bootstrap: 'success',
  auto_create: 'success',
  import: 'success',
  seed: 'success',
  update: 'info',
  adjust: 'info',
  bulk: 'info',
  sync: 'info',
  delete: 'danger',
  deactivate: 'danger',
};

function actionVariant(action) {
  if (!action) return 'default';
  for (const [key, variant] of Object.entries(ACTION_VARIANTS)) {
    if (action.includes(key)) return variant;
  }
  return 'default';
}

function buildMonthOptions() {
  const options = [];
  const now = new Date();
  for (let i = 0; i < 12; i++) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const value = `${year}_${month}`;
    const label = d.toLocaleString('en-US', { month: 'long', year: 'numeric' });
    options.push({ value, label });
  }
  return options;
}

export default function AuditLogPage() {
  const toast = useToast();
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [selectedMonth, setSelectedMonth] = useState(() => {
    const now = new Date();
    return `${now.getUTCFullYear()}_${String(now.getUTCMonth() + 1).padStart(2, '0')}`;
  });
  const [viewingEntry, setViewingEntry] = useState(null);

  const monthOptions = useMemo(() => buildMonthOptions(), []);

  async function load() {
    setLoading(true);
    try {
      const res = await adminApi.listAudit(selectedMonth);
      setEntries(res.entries || []);
    } catch (err) {
      toast.error(`Failed to load audit log: ${err.message}`);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load();
  }, [selectedMonth]); // eslint-disable-line react-hooks/exhaustive-deps

  const filtered = useMemo(() => {
    if (!search) return entries;
    const s = search.toLowerCase();
    return entries.filter(
      (e) =>
        e.actor?.toLowerCase().includes(s) ||
        e.action?.toLowerCase().includes(s) ||
        e.entityType?.toLowerCase().includes(s) ||
        e.entityId?.toLowerCase().includes(s)
    );
  }, [entries, search]);

  const columns = [
    {
      key: 'timestamp',
      label: 'Time',
      render: (e) => (
        <span className="whitespace-nowrap text-sm">{formatTimestamp(e.timestamp)}</span>
      ),
    },
    {
      key: 'actor',
      label: 'Actor',
      render: (e) => <span className="text-sm">{e.actor || 'system'}</span>,
    },
    {
      key: 'action',
      label: 'Action',
      render: (e) => (
        <Badge variant={actionVariant(e.action)}>{formatAction(e.action)}</Badge>
      ),
    },
    {
      key: 'entityType',
      label: 'Entity',
      render: (e) => (
        <div>
          <span className="text-sm">{e.entityType || '\u2014'}</span>
          {e.entityId && (
            <span className="ml-1.5 text-xs text-muted-foreground">
              {e.entityId.length > 24 ? e.entityId.substring(0, 24) + '...' : e.entityId}
            </span>
          )}
        </div>
      ),
    },
    {
      key: 'details',
      label: 'Details',
      render: (e) => {
        if (!e.details) return '\u2014';
        const summary = typeof e.details === 'object'
          ? Object.entries(e.details).slice(0, 2).map(([k, v]) => `${k}: ${v}`).join(', ')
          : String(e.details);
        return (
          <span className="text-xs text-muted-foreground">
            {summary.length > 50 ? summary.substring(0, 50) + '...' : summary}
          </span>
        );
      },
    },
  ];

  return (
    <div>
      <div className="mb-6">
        <h1 className="text-2xl font-bold tracking-tight">Audit Log</h1>
        <p className="text-muted-foreground">
          {entries.length} entries for {monthOptions.find((m) => m.value === selectedMonth)?.label || selectedMonth}
        </p>
      </div>

      <div className="mb-5 flex items-end gap-3">
        <Input
          label="Search"
          placeholder="Search by actor, action, entity..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="flex-1 max-w-[400px]"
        />
        <Select
          label="Month"
          value={selectedMonth}
          onChange={(e) => setSelectedMonth(e.target.value)}
          options={monthOptions}
        />
      </div>

      <DataTable
        columns={columns}
        rows={filtered}
        loading={loading}
        emptyMessage="No audit entries for this period."
        onRowClick={(e) => setViewingEntry(e)}
        keyFn={(e) => e.rowKey}
      />

      {viewingEntry && (
        <Modal
          isOpen={true}
          onClose={() => setViewingEntry(null)}
          title="Audit Entry Details"
          width="560px"
          footer={
            <Button variant="ghost" onClick={() => setViewingEntry(null)}>Close</Button>
          }
        >
          <div className="flex flex-col gap-3">
            <DetailRow label="Timestamp" value={viewingEntry.timestamp} />
            <DetailRow label="Actor" value={viewingEntry.actor} />
            <DetailRow label="Action" value={formatAction(viewingEntry.action)} />
            <DetailRow label="Entity Type" value={viewingEntry.entityType} />
            <DetailRow label="Entity ID" value={viewingEntry.entityId} />
            {viewingEntry.details && (
              <div>
                <div className="mb-1 text-xs text-muted-foreground">Details</div>
                <pre className="m-0 max-h-[300px] overflow-auto whitespace-pre-wrap rounded-md border border-input bg-muted/50 p-3 font-mono text-xs text-foreground">
                  {JSON.stringify(viewingEntry.details, null, 2)}
                </pre>
              </div>
            )}
          </div>
        </Modal>
      )}
    </div>
  );
}

function DetailRow({ label, value }) {
  return (
    <div>
      <div className="text-xs text-muted-foreground">{label}</div>
      <div className="text-sm">{value || '\u2014'}</div>
    </div>
  );
}
